import React from 'react';
import cx from 'classnames';
import { compact } from 'lodash';
import { Alert, Col, ControlLabel, Row } from 'react-bootstrap';

import RadioGroup from '@magoosh/lib/formik_inputs/field_radio_group';
import { toSentence } from 'utilities/formatting';

import StripeCardInputs from '@payments/components/inputs/stripe_card';
import { PaymentMethod, PaymentStyle, PaymentMethodsFormGroupProps } from '@payments/types';

import stripeLogo from './assets/powered_by_stripe.svg';
import style from '@payments/style.module.scss';

export const PaymentMethodsFormGroup: React.FC<PaymentMethodsFormGroupProps> = ({
  allowedPaymentMethods,
  formattedDiscountPrice,
  values,
  paymentRequestMethod
}) => {
  const paymentMethods = allowedPaymentMethods || compact([
    PaymentMethod.STRIPE,
    PaymentMethod.PAYPAL,
    PaymentMethod.ALIPAY,
    PaymentMethod.STRIPE_PAYMENT_REQUEST
  ]);

  const disabledPaymentsHint = () => {
    const disabledPaymentMethodNames = paymentMethods.filter((method) => method !== PaymentMethod.STRIPE).map((paymentMethod) => {
      if (paymentMethod === PaymentMethod.PAYPAL) {
        return 'PayPal';
      } else if (paymentMethod === PaymentMethod.ALIPAY) {
        return 'Alipay';
      } else if (paymentMethod === PaymentMethod.STRIPE_PAYMENT_REQUEST) {
        return paymentRequestMethod;
      } else {
        return paymentMethod;
      }
    });

    return `Select “Pay full ${formattedDiscountPrice} today” to pay with ${toSentence(disabledPaymentMethodNames, 'or')}`;
  }

  const paymentOptions = [
    {
      icons: ['fa fa-credit-card-alt'],
      value: PaymentMethod.STRIPE
    },
    {
      icons: ['fa fa-cc-paypal'],
      value: PaymentMethod.PAYPAL,
      disabled: values.paymentStyle === PaymentStyle.INSTALLMENTS,
      hint: values.paymentStyle === PaymentStyle.INSTALLMENTS && disabledPaymentsHint()
    },
    {
      icons: ['sprite sprite-alipay subscription-payment-sprite'],
      value: PaymentMethod.ALIPAY,
      disabled: values.paymentStyle === PaymentStyle.INSTALLMENTS
    }
  ].filter((method) => paymentMethods.indexOf(method.value) !== -1);

  // Add Apple/Google pay if available
  if (paymentRequestMethod && paymentMethods.indexOf(PaymentMethod.STRIPE_PAYMENT_REQUEST) !== -1) {
    let iconName = 'sprite';
    if (paymentRequestMethod == 'Google Pay') {
      iconName += ' sprite-google-pay';
    } else if (paymentRequestMethod == 'Apple Pay') {
      iconName += ' sprite-apple-pay';
    }

    paymentOptions.push({
      icons: [iconName],
      value: PaymentMethod.STRIPE_PAYMENT_REQUEST,
      disabled: values.paymentStyle === PaymentStyle.INSTALLMENTS
    });
  }

  return (
    <div className="form-group">
      <fieldset className="inputs">
        <legend className="u-margin-B-n">
          <span>Payment Method</span>
        </legend>
        <Row>
          <Col md={8}>
            {
              paymentMethods.length > 1 && (
                <RadioGroup
                  name="paymentMethod"
                  optionsArray={paymentOptions}
                  value={values.paymentMethod}
                  optionClassName={cx(style.payments, 'radio-inline')}
                />
              )
            }

            {values.paymentMethod === PaymentMethod.STRIPE && <StripeCardInputs />}

            {values.paymentMethod === PaymentMethod.PAYPAL && (
              <Alert bsStyle="secondary">
                Payment will be collected on PayPal after you click &quot;Continue to Payment&quot;
              </Alert>
            )}

            {values.paymentMethod === PaymentMethod.ALIPAY && (
              <Alert bsStyle="secondary">
                Payment will be collected on Alipay after you click &quot;Continue to Payment&quot;
              </Alert>
            )}
          </Col>
          {
            values.paymentMethod === PaymentMethod.STRIPE && (
              <Col md={4} className="hidden-sm hidden-xs text-center u-margin-T-m">
                <a href="http://www.stripe.com" target="_blank" rel="noopener noreferrer">
                  <img alt="Powered by Stripe" src={stripeLogo}></img>
                </a>
              </Col>
            )
          }
        </Row>
      </fieldset>
    </div>
  );
};

export default PaymentMethodsFormGroup;
